import React from 'react';
import { X, Keyboard } from 'lucide-react';

const SECTIONS = [
    {
        title: 'Global Navigation (G → key)',
        shortcuts: [
            { keys: ['G', 'C'], desc: 'Calendar' },
            { keys: ['G', 'S'], desc: 'Scheduling' },
            { keys: ['G', 'E'], desc: 'Master Data (Entities)' },
            { keys: ['G', 'A'], desc: 'Assignments' },
            { keys: ['G', 'T'], desc: 'Settings' },
        ],
    },
    {
        title: 'Calendar Panel',
        shortcuts: [
            { keys: ['D'], desc: 'Day view' },
            { keys: ['W'], desc: 'Week view' },
            { keys: ['M'], desc: 'Month view' },
            { keys: ['A'], desc: 'Agenda view' },
            { keys: ['T'], desc: 'Go to today' },
            { keys: ['E'], desc: 'New event' },
            { keys: ['/'], desc: 'Open search' },
            { keys: ['←', '→'], desc: 'Navigate dates' },
            { keys: ['['], desc: 'Toggle calendar sidebar' },
            { keys: [']'], desc: 'Toggle scheduler sidebar' },
        ],
    },
    {
        title: 'Data Panels',
        shortcuts: [
            { keys: ['↑', '↓'], desc: 'Navigate cards' },
            { keys: ['J', 'K'], desc: 'Navigate cards (vim)' },
            { keys: ['Enter'], desc: 'Select / open card' },
            { keys: ['E'], desc: 'Edit selected card' },
            { keys: ['Del'], desc: 'Delete selected card' },
        ],
    },
    {
        title: 'General',
        shortcuts: [
            { keys: ['Esc'], desc: 'Dismiss (modal → edit → selection)' },
            { keys: ['?'], desc: 'Toggle this cheat sheet' },
        ],
    },
];

function Kbd({ children }) {
    return (
        <kbd className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 text-[0.65rem] font-semibold text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 rounded-md shadow-[0_1px_0_1px_rgba(0,0,0,0.04)] font-mono">
            {children}
        </kbd>
    );
}

export default function ShortcutCheatSheet({ open, onClose }) {
    if (!open) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm animate-fade-in"
                onClick={onClose}
            />

            {/* Sheet */}
            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[201] w-full max-w-lg max-h-[80vh] overflow-y-auto bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border border-zinc-200 dark:border-zinc-700 animate-scale-in">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-700/50 sticky top-0 bg-white dark:bg-zinc-900 z-10">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                            <Keyboard className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-zinc-800 dark:text-zinc-100">Keyboard Shortcuts</h2>
                            <p className="text-[0.65rem] text-zinc-400 mt-0.5">Press ? to toggle this sheet</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center text-zinc-400 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {SECTIONS.map((section) => (
                        <div key={section.title}>
                            <h3 className="text-[0.65rem] uppercase tracking-widest text-zinc-400 font-semibold mb-3">
                                {section.title}
                            </h3>
                            <div className="space-y-1.5">
                                {section.shortcuts.map((shortcut) => (
                                    <div
                                        key={shortcut.desc}
                                        className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                                    >
                                        <span className="text-sm text-zinc-600 dark:text-zinc-300">{shortcut.desc}</span>
                                        <div className="flex items-center gap-1">
                                            {shortcut.keys.map((key, i) => (
                                                <React.Fragment key={i}>
                                                    {i > 0 && shortcut.keys.length === 2 && shortcut.keys[0] === 'G' && (
                                                        <span className="text-[0.55rem] text-zinc-400 mx-0.5">then</span>
                                                    )}
                                                    {i > 0 && !(shortcut.keys[0] === 'G') && (
                                                        <span className="text-[0.55rem] text-zinc-400 mx-0.5">/</span>
                                                    )}
                                                    <Kbd>{key}</Kbd>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-zinc-200 dark:border-zinc-700/50 text-center">
                    <p className="text-[0.6rem] text-zinc-400">
                        Shortcuts are disabled when typing in input fields
                    </p>
                </div>
            </div>
        </>
    );
}
